import React from 'react';

import { isServer } from './environment';

/**
 * Checks if a URL is relative
 * @param url - The URL to check
 * @returns `true` if the URL is relative, `false` otherwise
 *
 * @remarks Assumes that page relative URLs with no leading slash, i.e. "abc/123" are invalid and
 * treated as absolute URLs.
 */
export function isRelativeUrl(url: string): boolean {
  if (!url) {
    if (!isServer()) {
      // eslint-disable-next-line no-console
      console.error(`URL is undefined or empty: ${url}`);
    }

    return false;
  }

  if (url.startsWith('//')) {
    return false;
  }

  return url.startsWith('/') || url.startsWith('.') || url.startsWith('#');
}

/**
 * Checks if a URL points to an external domain
 * @param url - The URL to check
 * @returns `true` if the URL points to an external domain, `false` otherwise
 */
export function isExternalUrl(url: string): boolean {
  if (isRelativeUrl(url)) {
    return false;
  }

  try {
    const parsedCurrent = new URL(process.env.GATSBY_SITE_URL);
    const parsed = new URL(url, process.env.GATSBY_SITE_URL);

    return parsed.hostname !== parsedCurrent.hostname;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`Error parsing URL "${url}":`, e);
    return false;
  }
}

/**
 * Determines the appropriate anchor target attribute based on whether the URL is external or internal.
 * Returns '_blank' for external URLs (opening in a new tab/window) and '_self' for internal URLs.
 *
 * @param url - The URL string to analyze
 * @returns Either '_blank' for external URLs or '_self' for internal URLs
 */
export function getAnchorTarget(
  url: string,
): Extract<React.HTMLAttributeAnchorTarget, '_self' | '_blank'> {
  return isExternalUrl(url) ? '_blank' : '_self';
}

/**
 * Converts an absolute URL to a relative URL.
 *
 * @param url - The URL to convert. Can be absolute or relative.
 * @returns A relative URL string.
 *
 * @remarks If there is an error, the original URL is returned.
 */
export function convertToRelativeUrl(url: string): string {
  if (!url) {
    // eslint-disable-next-line no-console
    console.error('Cannot convert falsy URL');
    return url;
  }

  if (isRelativeUrl(url)) {
    return url;
  }

  try {
    const { pathname, search, hash } = new URL(url);
    return pathname + search + hash;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to convert URL to relative: "${url}"`, error);
    return url;
  }
}
